import { Draft, produce } from 'immer';

import * as SIGNING_METHOD_TYPE from '@app/constants/signingMethodType';
import removeDashAndSpaceIfPhoneNumber from '@app/libs/formatter/removeDashAndSpaceIfPhoneNumber';
import * as TYPES from '@app/store/actionTypes';
import { HostReference } from '@app/store/Host';
import MeansType from '@app/store/MeansType';
import {
  scheduledDocumentPreviewDataFetched,
  setTemplateParticipants,
  setupTemplateByDraft,
  TemplateAction,
} from '@app/store/template/actions';

export interface TemplateParticipantByIdState {
  [id: string]: {
    id: string;
    contactInfo?: string | null;
    means: string;
    meansType: MeansType;
    name: string;
    role: string;
    predefined?: boolean;
    host?: HostReference;
  };
}

const initialState: TemplateParticipantByIdState = {};

const reducer = produce((draft: Draft<TemplateParticipantByIdState>, action: TemplateAction) => {
  switch (action.type) {
    case TYPES.TEMPLATE_CLEAR_ALL_STATE:
    case TYPES.TEMPLATE_INFORMATION_FETCH_REQUEST:
      return initialState;

    case TYPES.TEMPLATE_INFORMATION_FETCH_SUCCESS: {
      const { participants } = action.payload;
      participants.forEach((participant) => {
        const { contactInfo, id, name, role, signingMethodType, host } = participant;
        let means = '';
        let meansType = MeansType.NONE;
        if (signingMethodType) {
          if (signingMethodType === SIGNING_METHOD_TYPE.KAKAO) {
            if (contactInfo) {
              means = contactInfo.replace(/^010/, '');
              meansType = MeansType.PHONE;
            }
          } else if (signingMethodType === SIGNING_METHOD_TYPE.IN_PERSON) {
            means = contactInfo || '';
            meansType = MeansType.IN_PERSON;
          } else {
            means = contactInfo || '';
            meansType = MeansType.EMAIL;
          }
        }
        if (id) {
          draft[id] = {
            id,
            means,
            meansType,
            name: name || '',
            role,
            predefined: !!(contactInfo || name),
            host,
          };
        }
      });
      return draft;
    }
    case setupTemplateByDraft.type: {
      action.payload.participants.forEach((participant) => {
        const {
          id,
          signingMethod: { type: meansType, value: means },
          name,
          role,
          host,
        } = participant;

        draft[id] = {
          id,
          means: meansType === MeansType.PHONE ? means?.replace(/^010/, '') : means,
          meansType,
          name: name ?? '',
          role: role ?? '',
          predefined: !!(means || name),
          host,
        };
      });
      return draft;
    }
    case setTemplateParticipants.type: {
      const res: TemplateParticipantByIdState = {};
      action.payload.forEach(({ id, role, means, meansType, name, host }) => {
        res[id] = {
          id,
          role: role.trim(),
          means: removeDashAndSpaceIfPhoneNumber(means?.trim()) ?? '',
          meansType: meansType ?? MeansType.NONE,
          name: name?.trim() ?? '',
          predefined: !!(means || name),
          host,
        };
      });

      return res;
    }
    case TYPES.TEMPLATE_PARTICIPANT_DELETE: {
      const { id } = action.payload;
      delete draft[id];
      return draft;
    }
    case scheduledDocumentPreviewDataFetched.type: {
      const newState: TemplateParticipantByIdState = {};
      action.payload.participants.forEach(({ id, name, contactInfo, role, host }) => {
        newState[id] = {
          id,
          role: role.trim(),
          means: contactInfo ?? '',
          contactInfo,
          meansType: MeansType.NONE,
          name: name ?? '',
          predefined: false,
          host,
        };
      });
      return newState;
    }
    default:
      return draft;
  }
}, initialState);

export default reducer;
